import React, { useState, useEffect } from 'react';
import { Progress, Button, Modal, Table } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import axios from 'axios';
import ENVIRONMENT from '../../../../environments';
import * as Utilities from '../../Utilities';
import './EnterpriseUsageDetails.scss';

export default function EnterpriseUsageDetails({ usageDetails, planData, openPricingPlansModal, openUpgradeModal, onBuyCredits, subscriptionAvailedEntities }) {
    
    const [usedCredits, setUsedCredits] = useState(0);
    const [totalCredits, setTotalCredits] = useState(0);
    const [entityDetailsModal, setEntityDetailsModal] = useState(false);
    const [isTableDataLoading, setIsTableDataLoading] = useState(false);
    const [entityTableData, setEntityTableData] = useState([]);
    const [currentEntityName, setCurrentEntityName] = useState('');

    const entityTableColumns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Entity ID",
            dataIndex: "entity_id",
            key: "entity_id",
        },
        {
            title: "Category",
            dataIndex: "category",
            key: "category",
        },
        {
            title: "Username",
            dataIndex: "customer_username",
            key: "customer_username",
        },
        {
            title: "Date",
            dataIndex: "request_time",
            key: "request_time",
        }
    ];

    const getSubscriptionDate = (date) => {
        return new Date(Date.parse(date)).toLocaleString('en-us', { year: 'numeric', month: 'short', day: 'numeric' });
    }

    useEffect(() => {
        console.log('subscriptionAvailedEntities', subscriptionAvailedEntities);
        if (planData?.['used_credits'] !== undefined) {
            setUsedCredits(planData['used_credits']);
        }
        if (planData?.['total_credits'] !== undefined) {
            setTotalCredits(planData['total_credits']);
        }
    }, [planData]);

    // Helper function to get used count from subscriptionAvailedEntities
    const getUsedCount = (requestType, category) => {
        if (!subscriptionAvailedEntities || !Array.isArray(subscriptionAvailedEntities)) {
            return 0;
        }
        const match = subscriptionAvailedEntities.find(
            (entity) => entity.request_type === requestType && entity.category === category
        );
        console.log(match);
        console.log('requestType', requestType);
        console.log('category', category);
        console.log('subscriptionAvailedEntities', subscriptionAvailedEntities);
        return match ? match.count : 0;
    };

    // Function to open entity details modal
    const openEntityDetails = (entityName, requestType, category) => {
        setCurrentEntityName(entityName);
        setEntityDetailsModal(true);
        setIsTableDataLoading(true);

        const payload = {
            'customer_username': localStorage.getItem('username'),
            'request_types': [requestType],
            'categories': category ? [category] : []
        };

        axios.post(ENVIRONMENT.GET_SUBSCRIPTION_AVAILED_ENTITIES, payload)
            .then((response) => {
                const dataRecords = response.data.map(row => ({
                    'name': row.name,
                    'entity_id': row.entity_id,
                    'category': row.category,
                    'customer_username': row.customer_username,
                    'request_time': Utilities.convertUtcToLocalTimezone(row.request_time)
                }));
                setEntityTableData(dataRecords);
                setIsTableDataLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching entity details:', error);
                setIsTableDataLoading(false);
            });
    };

    // Products section data
    const productsData = [
        { name: 'Product uploads', usedCount: getUsedCount('existing_product_model', ''), subtext: 'Unlimited', requestType: 'existing_product_model' },
        { name: 'Simple product models', usedCount: getUsedCount('product_model', ''), subtext: '13 credits each', requestType: 'product_model' },
        { name: 'Complex product models', usedCount: getUsedCount('complex_product_model', ''), subtext: '25 credits each', requestType: 'complex_product_model' },
        { name: 'Simple product variants', usedCount: getUsedCount('variation_product_model', ''), subtext: '6 credits each', requestType: 'variation_product_model' },
        { name: 'Complex product variants', usedCount: getUsedCount('complex_variation_product_model', ''), subtext: '13 credits each', requestType: 'complex_variation_product_model' },
        { name: 'Space/room models', usedCount: getUsedCount('room_model', ''), subtext: '20 credits each', requestType: 'room_model' },
        { name: 'AI models', usedCount: getUsedCount('ai_model', ''), subtext: '1 credits each', requestType: 'ai_model' }
    ];

    // Images section data
    const imagesData = [
        { name: 'AI image previews (1K)', usedCount: getUsedCount('ai_image_1k', ''), subtext: 'Unlimited', requestType: 'ai_image_1k' },
        { name: 'AI final images (4K)', usedCount: getUsedCount('ai_image_4k', ''), subtext: '4 credits each', requestType: 'ai_image_4k' },
        { name: 'AI final images (4K) + MSP', usedCount: getUsedCount('ai_image_4k_msp', ''), subtext: '12 credits each', requestType: 'ai_image_4k_msp' },
        { name: 'Silo images (4K)', usedCount: getUsedCount('product_silo', '4k'), subtext: '2 credits each (set of 4)', requestType: 'product_silo', category: '4k' },
        { name: 'Lifestyle images (2K)', usedCount: getUsedCount('scene_lifestyle_image', '2k'), subtext: '3 credits each', requestType: 'scene_lifestyle_image', category: '2k' },
        { name: 'Lifestyle images (4K)', usedCount: getUsedCount('scene_lifestyle_image', '4k'), subtext: '6 credits each', requestType: 'scene_lifestyle_image', category: '4k' },
        { name: 'Lifestyle images (4K) + MSP', usedCount: getUsedCount('scene_lifestyle_image_msp', ''), subtext: '20 credits each', requestType: 'scene_lifestyle_image_msp' }
    ];

    // Other assets section data
    const otherAssetsData = [
        { name: '360 spins', usedCount: getUsedCount('product_360_spin', '4k'), subtext: '8 credits each', requestType: 'product_360_spin' },
        { name: 'MP4 videos', usedCount: getUsedCount('product_video', '1k'), subtext: '7 credits each', requestType: 'product_video' },
        { name: '3D tours', usedCount: getUsedCount('3d_tour', '1k'), subtext: '7 credits each', requestType: '3d_tour' }
    ];

    const usageOrder = ['scene_lifestyle_image', 'ai_model', 'ai_image_1k', 'scene_lifestyle_images'];
    
    const usageSections = [
        { 
            title: 'Products', 
            items: productsData
        },
        { 
            title: 'Images', 
            items: imagesData
        },
        { 
            title: 'Other Assets', 
            items: otherAssetsData
        },
    ];

    const renderUsageDetail = (item, index) => {
        return (
            <div className='enterprise-usage-item' key={index}>
                <div className="justify-space-between">
                    <div className='justify-in-start usage-quota-detail-card'>
                        <span className='manrope f-14 lh-22 w-700 black-085' style={{ flex: 1, whiteSpace: "nowrap" }}>{item.name}</span>
                        <div style={{ flex: 2 }}></div>
                        <div style={{ flex: 1 }}>
                            {item.usedCount > 0 ? (
                                <a 
                                    className='manrope f-14 lh-22 w-500 black-085 blck-hover'
                                    onClick={() => openEntityDetails(item.name, item.requestType, item.category)}
                                    style={{ whiteSpace: "nowrap", cursor: 'pointer' }}
                                >
                                    {item.usedCount} used
                                </a>
                            ) : (
                                <span className='manrope f-14 lh-22 w-500 black-085' style={{ whiteSpace: "nowrap" }}>
                                    {item.usedCount} used
                                </span>
                            )}
                        </div>
                        <div style={{ flex: 1 }} className="justify-in-end">
                            <span className='manrope f-14 lh-22 w-500 black-085' style={{ whiteSpace: "nowrap", opacity: 0.6 }}>{item.subtext}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='enterprise-usage-container'>
            {/* Sidebar */}
            <div className='enterprise-usage-sidebar'>
                <span className='manrope f-16 w-700 lh-26 black-085'>Credits</span>
                <span className='manrope f-16 w-700 lh-26 black-085'>Usage</span>
            </div>

            {/* Content */}
            <div className='enterprise-usage-content'>
                {/* Overview Section */}
                <div>
                    <div className='manrope f-16 w-500 black-085'>Overview</div>
                    
                    <div className='enterprise-overview-content'>
                        <div className='enterprise-overview-item'>
                            <span className='manrope f-14 lh-22 w-700 black-085' style={{ minWidth: '100px' }}>Total credits</span>
                            <div className="flex-1" style={{ display: 'flex', alignItems: 'center', margin: '0 24px', flex: 1 }}>
                                <div style={{ flex: 1, marginRight: '12px' }}>
                                    <Progress percent={totalCredits > 0 ? (usedCredits / totalCredits) * 100 : 0} showInfo={false} strokeColor="#1890FF" />
                                </div>
                                <span className='manrope f-14 lh-22 black-085' style={{ whiteSpace: 'nowrap' }}>{usedCredits} of {totalCredits} used</span>
                            </div>
                            {usedCredits && totalCredits && usedCredits/totalCredits >= 0.9 ? (
                                <Button className='manrope f-14 w-500 lh-22 buy-credits-btn' onClick={onBuyCredits}>
                                    <img src={require('../../../../assets/images/diagonal-arrow.svg')} />
                                    Buy more credits
                                </Button>
                            ): null}
                        </div>
                    </div>
                </div>
                
                <hr className='enterprise-usage-divider' />

                {/* Products and Images Sections */}
                {usageSections.map((section) => (
                    <React.Fragment key={section.title}>
                        <div className='enterprise-usage-section'>
                            <div className='manrope f-16 w-500 lh-26 black-085'>{section.title}</div>
                            <div className='enterprise-usage-items' style={{ marginTop: '35px' }}>
                                {section.items.map((item, index) => renderUsageDetail(item, index))}
                            </div>
                        </div>
                        <hr className='enterprise-usage-divider' />
                    </React.Fragment>
                ))}
            </div>

            {/* Entity Details Modal */}
            <Modal
                title={currentEntityName}
                maskClosable={false}
                onCancel={() => setEntityDetailsModal(false)}
                visible={entityDetailsModal}
                width={"70%"}
                centered={true}
                style={{ paddingTop: 20, minWidth: "80%" }}
                footer={null}
            >
                {isTableDataLoading ? (
                    <LoadingOutlined className="loading-center" />
                ) : (
                    <Table
                        dataSource={entityTableData}
                        columns={entityTableColumns}
                        rowKey={(record) => record.entity_id}
                        scroll
                    />
                )}
            </Modal>
        </div>
    );
}
